'use client';

import { completeFirebaseSocialLogin } from '@/lib/auth/complete-firebase-social-login';
import {
    clearLoginPending,
    readOAuthLoginTrackingContext,
    readReturnToForOAuth,
    sanitizeReturnToForReplace,
} from '@/lib/auth/firebase-oauth-session';
import { signInWithFacebookAccessToken } from '@/lib/firebase/auth';
import { readArmsFailDetail } from '@/utils/arms/common';
import { reportLoginFail } from '@/utils/arms/login-events';

import { clearFacebookOAuthHashFromUrl, parseFacebookOAuthHash } from './oauth-redirect';

const DEFAULT_AFTER_LOGIN = '/profile';

export interface IProcessFacebookOAuthHashReturnParams {
    replace: (path: string) => void;
    onErrorToast: (message: string) => void;
}

/** 处理 /login#access_token=... 回跳；无 Facebook OAuth hash 时返回 false */
export async function processFacebookOAuthHashReturn(params: IProcessFacebookOAuthHashReturnParams): Promise<boolean> {
    const parsed = parseFacebookOAuthHash();
    if (!parsed) {
        return false;
    }

    clearFacebookOAuthHashFromUrl();

    const tracking = readOAuthLoginTrackingContext();
    const returnTo = readReturnToForOAuth();
    const { replace, onErrorToast } = params;

    if ('error' in parsed) {
        clearLoginPending();
        if (parsed.error !== 'access_denied') {
            reportLoginFail({
                login_method: tracking.login_method,
                trigger_scene: tracking.trigger_scene,
                fail_reason:
                    parsed.error === 'invalid_state' ? 'facebook_oauth_invalid_state' : 'facebook_oauth_hash_error',
                fail_detail: parsed.error,
            });
            onErrorToast('Login failed');
        }
        replace(sanitizeReturnToForReplace(returnTo, '/login'));
        return true;
    }

    try {
        const userCredential = await signInWithFacebookAccessToken(parsed.accessToken);
        const idToken = await userCredential.user.getIdToken();
        const result = await completeFirebaseSocialLogin({
            idToken,
            tracking,
            onErrorToast,
        });
        if (!result.success) {
            replace(sanitizeReturnToForReplace(returnTo, '/login'));
            return true;
        }
        replace(sanitizeReturnToForReplace(returnTo, DEFAULT_AFTER_LOGIN));
    } catch (err) {
        console.error('facebook oauth hash return error', err);
        reportLoginFail({
            login_method: tracking.login_method,
            trigger_scene: tracking.trigger_scene,
            fail_reason: 'facebook_firebase_credential_failed',
            fail_detail: readArmsFailDetail(err),
        });
        onErrorToast('Login failed');
        replace(sanitizeReturnToForReplace(returnTo, '/login'));
    } finally {
        clearLoginPending();
    }

    return true;
}
